import { defa }                   from 'defa-object';
import ConfigView                 from 'scripts/defa/basic/view-config-view';
import Customer                   from 'scripts/defa/customers/model-customer';
/**
 * defa.views.UnitsInstalledFlow
 * A flow for selecting an installer and viewing it's installed units.
 * @version 1.2.1
 */
import CustomerContainer          from 'scripts/defa/customers/view-customer-container';
import SearchResults              from 'scripts/defa/search-results/collection-search-results';
import SearchResultCollectionView from 'scripts/defa/search-results/view-search-result-collection-view';
import Unit                       from 'scripts/defa/units/model-unit';
import UnitSearchRegistrationForm from 'scripts/defa/units/view-unit-search-registration-form';
import UnitSearchSerialForm       from 'scripts/defa/units/view-unit-search-serial-form';
import PaginationStrip            from 'scripts/plugins/pagination-strip';
import Flow                       from 'scripts/poppet/flow';
import template                   from 'templates/unitsInstalledFlow.jst.html';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' ),
	channelUser   = Bb.Radio.channel( 'user' );

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	headline       : '[data-title=installed-headline]',
	regionSearch   : '[data-region=installed-search]',
	regionContainer: '[data-region=installed-container]',
	pagination     : '[data-element=pagination-strip]',
};

const apiHost = defa.vars.apiHost;

const api = {
	getUnits: apiHost + '/units/installer/%s1?page=%s2',
};

const UnitsInstalledFlow = Flow.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template  : template,
	attributes: {
		'data-layout': 'units-installed',
	},
	
	ui: {
		headline: selectors.headline,
	},
	
	regions: {
		search   : selectors.regionSearch,
		container: selectors.regionContainer,
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when a childView requests a nav event.
	 * @param {string} event  The event type to trigger the nav with.
	 * @void
	 */
	onChildviewNav: function ( event ) {
		
		switch ( event ) {
			
			case 'unit:search:serial' :
				this.attachSearchSerial();
				break;
			
			case 'unit:search:registration' :
				this.attachSearchRegistration();
				break;
		}
		
	},
	
	/**
	 * Triggers when a childView requests to open a search result.
	 * @param {number} id    The id of the search result target.
	 * @param {string}  type  The model type of the search result target.
	 */
	onChildviewSearchResultOpen: function ( id, type ) {
		this.trigger( 'nav', 'unit:control:settings', [ id, ] );
	},
	
	/**
	 * Triggers when a childView requests to open a customer.
	 * @param {number} id  The id of the customer.
	 * @void
	 */
	onChildviewInstallerOpen: function ( id ) {
		this.installer = id;
		this.updateUrl();
		this.dispatch();
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Updates the url to reflect on choices made by the installer, then calls dispatch.
	 * @void
	 */
	updateUrl: function () {
		channelNav.trigger( 'unit:installed:selected', [ this.installer, ( parseInt( this.page ) + 1 ) ], {} );
	},
	
	/**
	 * The dispatch flow for finding this installers installed units.
	 * @void
	 */
	dispatch: function () {
		let fn = !1;
		
		this.installer = this.installer || this.options.installer || !1;
		this.page      = this.page || this.options.page || 0;
		
		if ( !this.installer ) {
			fn = this._showSelectInstaller;
		}
		
		if ( this.installer && isNaN( this.page ) ) {
			this.page = 0;
		}
		
		if ( this.installer && !isNaN( this.page ) ) {
			this.attachSearch();
			fn = this._showInstalledUnits;
		}
		
		if ( _.isFunction( fn ) ) {
			fn.call( this );
		}
	},
	
	/**
	 * Attaches a search box based on the installer's roles.
	 * @void
	 */
	attachSearch: function () {
		let currentInstaller = channel.request( 'customers' ).get( this.installer );
		
		if ( ( currentInstaller instanceof Customer ) && ( currentInstaller.hasRoles( [ 'INSTALLER_AUTOMOTIVE', ] ) ) ) {
			this.attachSearchRegistration();
		} else {
			this.attachSearchSerial( {
				context: 'installer',
			} );
		}
	},
	
	/**
	 * Attaches the unit registry search to the dom.
	 * @void
	 */
	attachSearchRegistration: function () {
		
		let unit = new Unit();
		
		let unitSearchRegistrationForm = new UnitSearchRegistrationForm( {
			model: unit,
		} );
		
		this.listenTo( unit, 'sync', function ( model ) {
			this.trigger( 'settings', model.get( 'id' ) );
		} );
		
		this.showChildView( 'search', unitSearchRegistrationForm );
		
	},
	
	/**
	 * Attaches the unit serial search to the dom.
	 * @void
	 */
	attachSearchSerial: function ( options ) {
		options = options || {};
		
		let unit = new Unit();
		
		let unitSearchSerialForm = new UnitSearchSerialForm( {
			context: options.context || 'installer',
			model  : unit,
		} );
		
		this.listenTo( unit, 'sync', function ( model ) {
			this.trigger( 'settings', model.get( 'id' ) );
		} );
		
		this.showChildView( 'search', unitSearchSerialForm );
		
	},
	
	
	// visual functions
	
	/**
	 * Shows a selection of installers, (or autoselects the only installer)
	 * @void
	 */
	_showSelectInstaller: function () {
		
		let user       = channelUser.request( 'model' ),
			customers  = user.getCustomers(),
			installers = customers.filterByRoles( [ 'INSTALLER_HOME', 'INSTALLER_AUTOMOTIVE', ] );
		
		// has one customer, select that customer.
		if ( 1 === installers.length ) {
			this.installer = installers.first().get( 'id' );
			this.updateUrl();
			this.dispatch();
		}
		
		// has more customers, show customer selection.
		if ( installers.length >= 2 ) {
			
			// attach customer read, add to this.customer on select.
			let customerContainer = new CustomerContainer( {
				view      : 'customer:list',
				user      : this.user,
				context   : 'installer',
				create    : false, // disables the options to create a new customer.
				customers : installers,
				navHandler: 'internal',
			} );
			
			this.showChildView( 'container', customerContainer );
			
		}
	},
	
	/**
	 * Shows the current page of installed units.
	 * @void
	 */
	_showInstalledUnits: function () {
		
		// attach a waiting view, while we wait.
		let configView = new ConfigView( {
			context: 'wait',
		} );
		
		// poll the page
		this.showChildView( 'container', configView );
		
		let self = this,
			url  = api.getUnits
					  .replace( '%s1', this.installer )
					  .replace( '%s2', this.page );
		
		$.get( url )
		 .done( function ( response ) {
			
			 // set current page to the response page
			 self.page = response.currentPage;
			 self.updateUrl();
			
			 self._showPagination( response );
			 self.createSearchResults( response );
		 } )
		 .fail( function ( response ) {
			
			 channelLayout.trigger( 'error', response.responseJSON );
		 } );
	},
	
	/**
	 * Shows the pagination strip for an options object.
	 * @param {object} options  Object containing the currentPage and totalPages integers.
	 * @void
	 */
	_showPagination: function ( options ) {
		
		let self = this;
		
		// only show pagination if there are two or more totalPages
		if ( options.totalPages >= 2 ) {
			
			// create new pagination strip
			if ( !self.paginationStrip ) {
				
				self.paginationStrip = new PaginationStrip( selectors.pagination, options );
				
				$( selectors.pagination ).on( 'change', function ( value, id ) {
					self.page = id;
					self._showInstalledUnits();
				} );
				
				// update existing pagination strip
			} else {
				
				this.paginationStrip.settings( options );
				
			}
		}
	},
	
	/**
	 * Creates search results and populates the view with them.
	 * @param options          An object containing our page information.
	 * @param options.results  - the results to be shown on the page.
	 */
	createSearchResults: function ( options ) {
		
		let searchResults = new SearchResults( options.content );
		
		let searchResultCollectionView = new SearchResultCollectionView( {
			collection: searchResults,
		} );
		
		this.ui.headline.removeAttr( 'hidden' );
		this.showChildView( 'container', searchResultCollectionView );
	},
	
} );

export default UnitsInstalledFlow;